import { redirect } from '@tanstack/react-router';

import { getStoredUser } from '@shared/auth/auth-route';
import { isTokenValid } from '@shared/auth/token.store';
import type { Permission } from '@shared/types/permissions';

/**
 * Factory that returns a TanStack Router `beforeLoad` callback enforcing
 * module-level permission at route resolution time — before any component renders.
 *
 * Usage in router.tsx:
 * ```ts
 * const wealthRoute = createRoute({
 *   getParentRoute: () => appRoute,
 *   path: '/wealth',
 *   component: WealthPage,
 *   beforeLoad: requirePermission('wealth:read'),
 * });
 * ```
 *
 * The parent `appRoute.beforeLoad` already validates the JWT. By the time this
 * runs we know the token is valid — but the user object is read from localStorage
 * because React context is not available in `beforeLoad`.
 *
 * SuperAdmins bypass all permission checks.
 */
/* eslint-disable @typescript-eslint/no-deprecated -- This guard is the last
   reader of the mock-era `role`. It is mounted on ~28 routes and, because the
   API never sends `role`, always falls through to the allow branch; the real
   gate on sensitive routes is `requireRole`, which reads the token's `roles`
   claim. Kept as-is so removing it is a deliberate router change, not a
   side effect of typing `User` honestly. */
export function requirePermission(permission: Permission | Permission[]) {
  const perms = Array.isArray(permission) ? permission : [permission];

  return (): void => {
    const user = getStoredUser();

    // Should not happen — appRoute.beforeLoad redirects unauthenticated users
    // to /login before any module beforeLoad runs. Guard defensively anyway.
    // Also guard against stale localStorage that may lack the `role` field.
    if (!user?.role) {
      // Fresh login: token is valid but user not yet persisted to localStorage.
      // AuthProvider will hydrate the user on mount — allow through.
      if (isTokenValid()) return;
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }

    if (user.role.isSuperAdmin) return;

    if (!perms.some((p) => user.role.permissions.includes(p))) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/unauthorized' });
    }
  };
}
